import { useState, useEffect, useCallback } from "react";
import { Table, Button, message, Tag } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { fetchWalletCashTopUps, approveWalletCashTopUp } from "../../apis/pro_forma_invoice.api";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";

interface WalletTopUp {
  id: number;
  client_id: string;
  client_name: string | null;
  client_phone: string | null;
  amount: string;
  payment_method: string | null;
  reference_id: string | null;
  created_at: string;
  created_by: string | null;
}

export default function WalletTopUps() {
  const { hasPermission } = usePermissions();
  const [data, setData] = useState<WalletTopUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchWalletCashTopUps()
      .then((res) => setData(Array.isArray(res) ? res : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id: number) => {
    if (!hasPermission("finance.create")) {
      message.error("You don't have permission to approve top-ups");
      return;
    }
    setApprovingId(id);
    const res = await approveWalletCashTopUp(id);
    if (res) {
      message.success("Wallet top-up approved and synced to Zoho.");
      load();
    }
    setApprovingId(null);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Client",
      key: "client",
      render: (_: any, row: WalletTopUp) => row.client_name ?? "—",
    },
    {
      title: "Phone",
      dataIndex: "client_phone",
      key: "client_phone",
      render: (val: string | null) => val ?? "—",
    },
    {
      title: "Amount (₹)",
      dataIndex: "amount",
      key: "amount",
      render: (val: string) => `₹ ${(Number(val) / 100).toFixed(2)}`,
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (val: string | null) => val ?? "—",
    },
    {
      title: "Reference",
      dataIndex: "reference_id",
      key: "reference_id",
      render: (val: string | null) => val ?? "—",
    },
    {
      title: "Added By",
      dataIndex: "created_by",
      key: "created_by",
      render: (val: string | null) => val ?? "—",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => new Date(val).toLocaleDateString("en-IN"),
    },
    {
      title: "Status",
      key: "status",
      render: () => <Tag color="orange">Pending Approval</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, row: WalletTopUp) => (
        <Button
          type="primary"
          size="small"
          icon={<CheckOutlined />}
          loading={approvingId === row.id}
          onClick={() => handleApprove(row.id)}
        >
          Approve
        </Button>
      ),
    },
  ];

  return (
    <div className="mt-3">
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No pending wallet top-ups" }}
      />
    </div>
  );
}
